import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNetworkStore } from "../stores/network";
import { useSlotTiming } from "../stores/slot-timing";
import { useWsStatus, WsState } from "../stores/ws-status";
import { CHART_SCOPES, useLiveCharts } from "../stores/live-charts";
import { useChartCatchUp } from "./useChartCatchUp";
import { liveProductionStatsKey } from "../api/hooks";
import { ws } from "../lib/ws";

export function useWebSocket() {
  const queryClient = useQueryClient();
  const { selectedNetwork } = useNetworkStore();
  const setTiming = useSlotTiming((s) => s.setTiming);
  const setWsState = useWsStatus((s) => s.setState);
  const liveCharts = useLiveCharts((s) => s.live);
  const catchUp = useChartCatchUp();

  useEffect(() => {
    ws.setStatusCallback((connected) => setWsState(connected ? WsState.Connected : WsState.Disconnected));
    // Delay connection slightly to avoid "interrupted while loading" on page refresh
    const timer = setTimeout(() => ws.connect(), 100);

    return () => {
      clearTimeout(timer);
      ws.disconnect();
    };
  }, [setWsState]);

  useEffect(() => {
    if (!selectedNetwork) return;
    ws.subscribe(selectedNetwork);

    const unsubs = [
      ws.on("subscribed", (data) => {
        const timing = (data as Record<string, unknown>).slotTiming as { genesisTime: number; slotDuration: number } | null;
        if (timing) setTiming(timing.genesisTime, timing.slotDuration);
      }),

      ws.on("health", (data) => {
        queryClient.setQueryData(["networks", selectedNetwork, "health", "current"], data);
      }),

      ws.on("new_blocks", (data) => {
        // Lists and headline counters always follow the head: a row appearing at the top of a
        // table or a total ticking up costs the reader nothing.
        queryClient.invalidateQueries({ queryKey: ["networks", selectedNetwork, "txs"] });
        queryClient.invalidateQueries({ queryKey: ["networks", selectedNetwork, "stats"] });
        // A live production window ends at the head, so every batch of blocks moves it —
        // including the epoch strip, whose last bar is the epoch currently filling. The
        // `true` segment is the hook's liveness flag: pinned ranges key `false` and are
        // left where the reader put them.
        queryClient.invalidateQueries({ queryKey: liveProductionStatsKey(selectedNetwork) });

        // Recorded either way, so a paused page can say how far behind it has fallen.
        const head = Number(data.to);
        if (Number.isFinite(head)) useLiveCharts.getState().noteChainHead(head);

        // The charts themselves only follow the head when asked to — see stores/live-charts.
        if (useLiveCharts.getState().live) {
          for (const scope of CHART_SCOPES) {
            queryClient.invalidateQueries({ queryKey: ["networks", selectedNetwork, scope] });
          }
        }
      }),

      ws.on("finality_update", () => {
        queryClient.invalidateQueries({ queryKey: ["networks", selectedNetwork, "checkpoints"] });
      }),

      ws.on("analysis_status", (data) => {
        const running = (data as Record<string, unknown>).running as boolean;
        queryClient.setQueryData(
          ["networks", selectedNetwork, "analyze-status"],
          (prev: Record<string, unknown> | undefined) => prev ? { ...prev, running } : prev,
        );
      }),

      ws.on("chain_pruned", () => {
        queryClient.invalidateQueries({ queryKey: ["networks", selectedNetwork] });
      }),
    ];

    return () => {
      unsubs.forEach((fn) => fn());
    };
  }, [selectedNetwork, queryClient, setTiming]);

  // Turning the charts live again catches them up immediately: the paused window can be
  // arbitrarily far behind, and waiting for the next block to arrive would leave the toolbar
  // claiming to be live while showing an old range. Only the transition triggers it — a fresh
  // mount fetches on its own.
  const wasLive = useRef(liveCharts);
  useEffect(() => {
    const turnedLive = liveCharts && !wasLive.current;
    wasLive.current = liveCharts;
    if (turnedLive) catchUp();
  }, [liveCharts, catchUp]);
}
